import React from 'react';
import { InputGroup } from '@blueprintjs/core';
import { useInfiniteAPI } from 'polotno/utils/use-api';
import { SectionTab } from 'polotno/side-panel';
import { ImagesGrid } from 'polotno/side-panel/images-grid';
import { selectImage } from 'polotno/side-panel/select-image';
import { unsplashList, unsplashDownload } from 'polotno/utils/api';
import { FaImage } from 'react-icons/fa';
import { observer } from 'mobx-react-lite';

const FALLBACK_MEDIA_QUERY = 'real estate';

interface PhotosPanelProps {
	store: any;
	defaultQuery?: string;
	t: (key: string, defaultValue?: string | Record<string, unknown>) => string;
}

export const PhotosPanel = observer<PhotosPanelProps>(({ store, defaultQuery = FALLBACK_MEDIA_QUERY, t }) => {
	const resolvedDefaultQuery = defaultQuery?.trim() || FALLBACK_MEDIA_QUERY;
	const { setQuery, loadMore, hasMore, data, isLoading, error } =
		useInfiniteAPI({
			defaultQuery: resolvedDefaultQuery,
			getAPI: ({ page, query }) => unsplashList({ page, query }),
		});

	return (
		<div className="h-100 d-flex flex-column">
			<InputGroup
				className="mb-3"
				leftIcon="search"
				placeholder={t('searchImages')}
				onChange={(e) => {
					const nextValue = e.target.value?.trim();
					setQuery(nextValue || resolvedDefaultQuery);
				}}
				type="search"
			/>
			<p className="text-center">
				{t('imagesFrom')}{' '}
				<a href="https://unsplash.com/" target="_blank" rel="noreferrer noopener">
					{t('unsplash')}
				</a>
			</p>
			<ImagesGrid
				images={data?.map((item) => item.results).flat().filter(Boolean)}
				getPreview={(image) => image.urls.small}
				getAlt={(image) => image.alt_description || ''}
				onSelect={async (image, droppedPos, targetElement) => {
					void fetch(unsplashDownload(image.id)).catch(() => {});
					await selectImage({
						src: image.urls.regular,
						store,
						droppedPos,
						targetElement,
					});
				}}
				isLoading={isLoading}
				error={error}
				loadMore={hasMore && loadMore}
				getCredit={(image) => (
					<span>
						{t('imagesFrom')}{' '}
						<a
							href={`https://unsplash.com/@${image.user.username}?utm_source=polotno&utm_medium=referral`}
							target="_blank"
							rel="noreferrer noopener"
						>
							{image.user.name}
						</a>{' '}
						<a
							href="https://unsplash.com/?utm_source=polotno&utm_medium=referral"
							target="_blank"
							rel="noreferrer noopener"
						>
							{t('unsplash')}
						</a>
					</span>
				)}
			/>
		</div>
	);
});

export const Photos = {
	name: 'photos',
	Tab: (props: any) => (
		<SectionTab {...props} name={props.name || 'Images'}>
			<FaImage />
		</SectionTab>
	),
	Panel: PhotosPanel,
};
